import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DailyMIT, Task, Block } from '@/types';
import { modalVariants, backdropVariants } from '@/lib/animations';
import styles from './MITSelector.module.css';

const MIT_COLLAPSED_KEY = 'al-time-block-mit-collapsed';

interface MITSelectorProps {
  mits: DailyMIT[];
  tasks: Task[];
  blocks: Block[];
  onAddMIT: (mit: { title: string; taskId?: string; blockId?: string; order: number }) => void;
  onCompleteMIT: (mitId: string) => void;
  onRemoveMIT: (mitId: string) => void;
  onConvertToTask?: (mit: DailyMIT) => void;
}

type DropdownMode = 'tasks' | 'blocks' | 'custom' | null;

interface DropdownState {
  isOpen: boolean;
  slotOrder: number | null;
  mode: DropdownMode;
}

export function MITSelector({
  mits,
  tasks,
  blocks,
  onAddMIT,
  onCompleteMIT,
  onRemoveMIT,
  onConvertToTask,
}: MITSelectorProps) {
  const [dropdown, setDropdown] = useState<DropdownState>({
    isOpen: false,
    slotOrder: null,
    mode: null,
  });
  const [customTitle, setCustomTitle] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(MIT_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(MIT_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    }

    if (dropdown.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdown.isOpen]);

  // Focus input when switching to custom mode
  useEffect(() => {
    if (dropdown.mode === 'custom' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [dropdown.mode]);

  const closeDropdown = useCallback(() => {
    setDropdown({ isOpen: false, slotOrder: null, mode: null });
    setCustomTitle('');
  }, []);

  const openDropdown = useCallback((slotOrder: number) => {
    setDropdown({ isOpen: true, slotOrder, mode: null });
  }, []);

  const setMode = useCallback((mode: DropdownMode) => {
    setDropdown((prev) => ({ ...prev, mode }));
  }, []);

  const handleSelectTask = useCallback(
    (task: Task) => {
      if (dropdown.slotOrder === null) return;
      onAddMIT({
        title: task.title,
        taskId: task.id,
        order: dropdown.slotOrder,
      });
      closeDropdown();
    },
    [dropdown.slotOrder, onAddMIT, closeDropdown]
  );

  const handleSelectBlock = useCallback(
    (block: Block) => {
      if (dropdown.slotOrder === null) return;
      onAddMIT({
        title: block.title,
        blockId: block.id,
        order: dropdown.slotOrder,
      });
      closeDropdown();
    },
    [dropdown.slotOrder, onAddMIT, closeDropdown]
  );

  const handleAddCustom = useCallback(() => {
    if (dropdown.slotOrder === null || !customTitle.trim()) return;
    onAddMIT({
      title: customTitle.trim(),
      order: dropdown.slotOrder,
    });
    closeDropdown();
  }, [dropdown.slotOrder, customTitle, onAddMIT, closeDropdown]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleAddCustom();
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    },
    [handleAddCustom, closeDropdown]
  );

  // Get MIT for a specific slot order
  const getMITForSlot = (order: number): DailyMIT | undefined => {
    return mits.find((mit) => mit.order === order);
  };

  // Calculate completion progress
  const completedCount = mits.filter((mit) => mit.completed).length;
  const totalMITs = mits.length;

  // Filter available tasks and blocks (not already assigned as MITs)
  const assignedTaskIds = new Set(mits.filter((m) => m.taskId).map((m) => m.taskId));
  const assignedBlockIds = new Set(mits.filter((m) => m.blockId).map((m) => m.blockId));

  const availableTasks = tasks.filter(
    (t) => t.status !== 'completed' && !assignedTaskIds.has(t.id)
  );
  const availableBlocks = blocks.filter(
    (b) => b.status !== 'completed' && !assignedBlockIds.has(b.id)
  );

  return (
    <div className={`${styles.container} ${isCollapsed ? styles.collapsed : ''}`}>
      {/* Header - clickable to toggle */}
      <button
        className={styles.header}
        onClick={toggleCollapsed}
        aria-expanded={!isCollapsed}
        aria-controls="mit-slots"
      >
        <h3 className={styles.title}>
          <span className={styles.starIcon} aria-hidden="true">
            ★
          </span>
          Today's MITs
        </h3>
        <div className={styles.headerRight}>
          <span className={styles.progress}>
            {completedCount}/{totalMITs} completed
          </span>
          <span className={`${styles.collapseIcon} ${isCollapsed ? styles.rotated : ''}`}>▼</span>
        </div>
      </button>

      {/* MIT Slots - collapsible */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            id="mit-slots"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            style={{ overflow: 'hidden' }}
          >
            <div className={styles.slots} role="list" aria-label="Most Important Tasks">
              {[1, 2, 3].map((slotOrder) => {
                const mit = getMITForSlot(slotOrder);
                const isDropdownOpen = dropdown.isOpen && dropdown.slotOrder === slotOrder;

                return (
                  <div
                    key={slotOrder}
                    className={styles.slotWrapper}
                    role="listitem"
                    ref={isDropdownOpen ? dropdownRef : undefined}
                  >
                    <motion.div
                      className={`${styles.slot} ${mit?.completed ? styles.completed : ''}`}
                      initial={false}
                      animate={{ scale: mit?.completed ? 0.98 : 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    >
                      {/* Star with number */}
                      <div className={styles.starBadge} aria-hidden="true">
                        <span className={styles.star}>★</span>
                        <span className={styles.starNumber}>{slotOrder}</span>
                      </div>

                      {mit ? (
                        /* Filled slot */
                        <div className={styles.filledSlot}>
                          <button
                            className={styles.checkbox}
                            onClick={() => onCompleteMIT(mit.id)}
                            aria-label={
                              mit.completed
                                ? `Mark "${mit.title}" as incomplete`
                                : `Mark "${mit.title}" as complete`
                            }
                            aria-pressed={mit.completed}
                          >
                            <motion.span
                              className={styles.checkboxInner}
                              initial={false}
                              animate={{
                                scale: mit.completed ? 1 : 0,
                                opacity: mit.completed ? 1 : 0,
                              }}
                              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            >
                              ✓
                            </motion.span>
                          </button>

                          <span
                            className={`${styles.mitTitle} ${mit.completed ? styles.strikethrough : ''}`}
                          >
                            {mit.title}
                          </span>

                          <div className={styles.mitActions}>
                            {/* Show convert button for standalone MITs (not linked to a task) */}
                            {!mit.taskId && onConvertToTask && (
                              <button
                                className={styles.convertBtn}
                                onClick={() => onConvertToTask(mit)}
                                aria-label={`Convert "${mit.title}" to a task`}
                                title="Convert to Task"
                              >
                                ↗
                              </button>
                            )}
                            <button
                              className={styles.removeBtn}
                              onClick={() => onRemoveMIT(mit.id)}
                              aria-label={`Remove "${mit.title}" from MITs`}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Empty slot */
                        <button
                          className={styles.addButton}
                          onClick={() => openDropdown(slotOrder)}
                          aria-expanded={isDropdownOpen}
                          aria-haspopup="true"
                        >
                          <span className={styles.addIcon}>+</span>
                          <span>Add MIT</span>
                        </button>
                      )}
                    </motion.div>
                  </div>
                );
              })}
            </div>

            {/* Progress bar */}
            {totalMITs > 0 && (
              <div className={styles.progressBar}>
                <motion.div
                  className={styles.progressFill}
                  initial={{ width: 0 }}
                  animate={{ width: `${(completedCount / 3) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* MIT Selection Popup Modal */}
      <AnimatePresence>
        {dropdown.isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className={styles.overlay}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={closeDropdown}
              aria-hidden="true"
            />

            {/* Modal */}
            <motion.div
              ref={dropdownRef}
              className={styles.popup}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mit-popup-title"
            >
              {/* Header */}
              <div className={styles.popupHeader}>
                {dropdown.mode !== null && (
                  <button className={styles.backBtn} onClick={() => setMode(null)}>
                    ←
                  </button>
                )}
                <h3 id="mit-popup-title" className={styles.popupTitle}>
                  <span className={styles.popupStar}>★</span>
                  {dropdown.mode === null
                    ? 'Add MIT'
                    : dropdown.mode === 'tasks'
                      ? 'Select Task'
                      : dropdown.mode === 'blocks'
                        ? 'Select Block'
                        : 'Create MIT'}
                </h3>
                <button className={styles.closeBtn} onClick={closeDropdown} aria-label="Close">
                  ×
                </button>
              </div>

              {/* Body */}
              <div className={styles.popupBody}>
                {dropdown.mode === null ? (
                  /* Mode selection */
                  <div className={styles.modeSelect}>
                    <button
                      className={styles.modeBtn}
                      onClick={() => setMode('tasks')}
                      disabled={availableTasks.length === 0}
                    >
                      <span className={styles.modeIcon}>☑</span>
                      <span>From Tasks</span>
                      <span className={styles.modeCount}>({availableTasks.length})</span>
                    </button>
                    <button
                      className={styles.modeBtn}
                      onClick={() => setMode('blocks')}
                      disabled={availableBlocks.length === 0}
                    >
                      <span className={styles.modeIcon}>▣</span>
                      <span>From Blocks</span>
                      <span className={styles.modeCount}>({availableBlocks.length})</span>
                    </button>
                    <button className={styles.modeBtn} onClick={() => setMode('custom')}>
                      <span className={styles.modeIcon}>✎</span>
                      <span>Create New</span>
                    </button>
                  </div>
                ) : dropdown.mode === 'tasks' ? (
                  /* Task selection */
                  <div className={styles.listItems}>
                    {availableTasks.length === 0 ? (
                      <div className={styles.emptyState}>No available tasks</div>
                    ) : (
                      availableTasks.map((task) => (
                        <button
                          key={task.id}
                          className={styles.listItem}
                          onClick={() => handleSelectTask(task)}
                        >
                          <span className={styles.itemTitle}>{task.title}</span>
                          {task.estimatedDuration && (
                            <span className={styles.itemMeta}>
                              {formatDuration(task.estimatedDuration)}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                ) : dropdown.mode === 'blocks' ? (
                  /* Block selection */
                  <div className={styles.listItems}>
                    {availableBlocks.length === 0 ? (
                      <div className={styles.emptyState}>No available blocks</div>
                    ) : (
                      availableBlocks.map((block) => (
                        <button
                          key={block.id}
                          className={styles.listItem}
                          onClick={() => handleSelectBlock(block)}
                        >
                          <span
                            className={styles.blockDot}
                            style={{ background: block.color }}
                            aria-hidden="true"
                          />
                          <span className={styles.itemTitle}>
                            {block.title || 'Untitled Block'}
                          </span>
                          <span className={styles.itemMeta}>
                            {formatTimeFromMinutes(block.startTime)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  /* Custom input */
                  <div className={styles.customInput}>
                    <input
                      ref={inputRef}
                      type="text"
                      className={styles.input}
                      placeholder="What's most important today?"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                    <button
                      className={styles.submitBtn}
                      onClick={handleAddCustom}
                      disabled={!customTitle.trim()}
                    >
                      Add MIT
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper functions
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatTimeFromMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

export default MITSelector;
